// Considered: SubscribeButtonAtom — rejected because canonical-atom policy requires ButtonAtom for all actions. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import { Channel } from 'types/channel';
import { MarketplaceMetrics } from 'types/marketplace';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import { deriveSubscribeButtonState } from '../../atoms/marketplace/SubscribeButtonAtom';
import { MARKETPLACE_SCOPE_ICONS } from '../../atoms/marketplace/iconMaps';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ChannelCardMolecule.scss';

export type ChannelCardDensity = 'roomy' | 'compact' | 'list';

export interface ChannelCardMoleculeProps {
  channel: Channel;
  density?: ChannelCardDensity;
  itemCount?: number;
  subscriberCount?: number;
  metrics?: MarketplaceMetrics | null;
  isSubscribed: boolean;
  isMandatory: boolean;
  isOwner: boolean;
  subscriptionLoading?: boolean;
  /** Pre-translated owner line, e.g. "by Acme Corp". Organism formats this. */
  ownerLabel?: string;
  /** Pre-translated "N subs · M items" line. Organism formats this. */
  subsItemsLabel?: string;
  /** Numeric rating 0..5. When > 0, renders StarDisplayAtom. */
  ratingAverage?: number;
  /** Pre-translated rating count label. */
  ratingCountLabel?: string;
  /** Omit in public/anon mode — the subscribe affordance is hidden when absent. */
  onSubscribeToggle?: (channelId: string, isCurrentlySubscribed: boolean) => void;
  onClick: (channelId: string) => void;
  className?: string;
}

const MAX_VISIBLE_TAGS = 3;

const STATUS_TONE_MAP = {
  pending_review: { tone: 'warn' as const, icon: 'pi pi-clock' },
  draft: { tone: 'info' as const, icon: 'pi pi-file-edit' },
  rejected: { tone: 'danger' as const, icon: 'pi pi-times-circle' },
  suspended: { tone: 'danger' as const, icon: 'pi pi-ban' },
};

export const ChannelCardMolecule: React.FC<ChannelCardMoleculeProps> = ({
  channel,
  density = 'roomy',
  itemCount,
  subscriberCount,
  metrics,
  isSubscribed,
  isMandatory,
  isOwner,
  subscriptionLoading = false,
  ownerLabel,
  subsItemsLabel,
  ratingAverage,
  ratingCountLabel,
  onSubscribeToggle,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();

  const subscribeState = deriveSubscribeButtonState({
    isOwner,
    status: channel.status,
    isSubscribed,
    isMandatory,
  });

  const displayName = getLocalizedText(channel.name);
  const displayDescription = getLocalizedText(channel.description);
  const tags = channel.tags ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = Math.max(0, tags.length - MAX_VISIBLE_TAGS);

  const effectiveRating = ratingAverage ?? metrics?.rating_average ?? 0;
  const ratingCount = metrics?.rating_count ?? 0;
  const subCount = subscriberCount ?? metrics?.subscriber_count;
  const hasRating = effectiveRating > 0;

  const ownerStatusEntry =
    isOwner && channel.status !== 'approved'
      ? STATUS_TONE_MAP[channel.status as keyof typeof STATUS_TONE_MAP] ?? null
      : null;

  const SUBSCRIBE_BTN_CONFIG = {
    not_subscribed: {
      label: t('marketplace.card.subscribe'),
      variant: 'secondary' as const,
      disabled: false,
    },
    subscribed: {
      label: t('marketplace.card.subscribed'),
      variant: 'secondary' as const,
      disabled: false,
    },
    mandatory: {
      label: t('marketplace.card.subscribed'),
      variant: 'secondary' as const,
      disabled: true,
    },
    manage: {
      label: t('marketplace.card.manage'),
      variant: 'ghost' as const,
      disabled: false,
    },
    pending_review: {
      label: t('marketplace.card.pendingReview'),
      variant: 'secondary' as const,
      disabled: true,
    },
    rejected: {
      label: t('marketplace.card.rejected'),
      variant: 'danger' as const,
      disabled: false,
    },
  };

  const btnConfig = SUBSCRIBE_BTN_CONFIG[subscribeState];

  const handleCardClick = () => onClick(channel.id);
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(channel.id);
    }
  };
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const nameId = `marketplace-channel-card__name-${channel.id}`;

  const rootClasses = [
    'marketplace-channel-card',
    `marketplace-channel-card--${density}`,
    `marketplace-channel-card--scope-${channel.scope}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const defaultStatsNode = (
    <span className="marketplace-channel-card__meta">
      {subCount != null && (
        <span className="marketplace-channel-card__subscriber-count">
          <i
            className="pi pi-users marketplace-channel-card__meta-icon"
            aria-hidden="true"
          />
          {t('marketplace.common.subscriberCount', { count: subCount })}
        </span>
      )}
      {subCount != null && itemCount != null && (
        <span className="marketplace-channel-card__separator" aria-hidden="true">
          &middot;
        </span>
      )}
      {itemCount != null && (
        <span className="marketplace-channel-card__item-count">
          <i
            className="pi pi-box marketplace-channel-card__meta-icon"
            aria-hidden="true"
          />
          {t('marketplace.common.itemCount', { count: itemCount })}
        </span>
      )}
    </span>
  );

  const actionStateClass = (() => {
    switch (subscribeState) {
      case 'subscribed':
      case 'mandatory':
        return 'marketplace-channel-card__actions--have';
      case 'not_subscribed':
        return 'marketplace-channel-card__actions--get';
      default:
        return '';
    }
  })();

  // Hidden in public/anon mode (no onSubscribeToggle handler supplied).
  const subscribeButton = onSubscribeToggle ? (
    <div
      className={`marketplace-channel-card__actions ${actionStateClass}`.trim()}
      onClick={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ButtonAtom
        label={btnConfig.label}
        variant={btnConfig.variant}
        disabled={btnConfig.disabled || subscriptionLoading}
        size="small"
        data-testid="channel-card-subscribe-btn"
      />
    </div>
  ) : null;

  const ratingBlock = (
    <span className="marketplace-channel-card__rating">
      <StarDisplayAtom rating={effectiveRating} size="small" />
      {hasRating && (
        <span className="marketplace-channel-card__rating-count">
          {ratingCountLabel ??
            t('marketplace.ratings.ratingCount', { count: ratingCount })}
        </span>
      )}
    </span>
  );

  if (density === 'list') {
    return (
      <article
        className={rootClasses}
        role="button"
        tabIndex={0}
        aria-labelledby={nameId}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        {channel.logoUrl ? (
          <img
            src={channel.logoUrl}
            alt={t('marketplace.channels.card.logoAlt', { name: displayName })}
            className="marketplace-channel-card__logo-img"
          />
        ) : (
          <ScopeCoinAtom
            scope={channel.scope}
            glyph={channel.glyph ?? null}
            channelName={displayName}
            size="sm"
            glyphKind={channel.glyph_kind ?? 'icon'}
            hue={channel.hue}
            isSystem={channel.is_system}
            className="marketplace-channel-card__coin"
          />
        )}
        <div className="marketplace-channel-card__title-col">
          <h3 id={nameId} className="marketplace-channel-card__name">
            {displayName}
          </h3>
          {displayDescription && (
            <p className="marketplace-channel-card__description">
              {displayDescription}
            </p>
          )}
        </div>
        <div className="marketplace-channel-card__scope-col">
          <ChipAtom
            label={t(`marketplace.common.scope.${channel.scope}`)}
            icon={MARKETPLACE_SCOPE_ICONS[channel.scope]}
            className={`marketplace-channel-card__scope-chip marketplace-channel-card__scope-chip--${channel.scope}`}
          />
        </div>
        <div className="marketplace-channel-card__publisher-col">
          {channel.publisher ? (
            <span className="marketplace-channel-card__attribution">
              <span className="marketplace-channel-card__attribution-by">by</span>
              <span className="marketplace-channel-card__attribution-name">
                {channel.publisher.display_name}
              </span>
              {channel.publisher.is_official && (
                <i
                  className="pi pi-verified marketplace-channel-card__attribution-verified"
                  aria-hidden="true"
                />
              )}
            </span>
          ) : (
            ownerLabel &&
            ownerLabel.trim().length > 0 && (
              <span className="marketplace-channel-card__owner-label">
                {ownerLabel}
              </span>
            )
          )}
        </div>
        <div className="marketplace-channel-card__stats-col">
          {ratingBlock}
          {subsItemsLabel ? (
            <span className="marketplace-channel-card__subs-items">
              {subsItemsLabel}
            </span>
          ) : (
            defaultStatsNode
          )}
        </div>
        {subscribeButton}
      </article>
    );
  }

  if (density === 'compact') {
    return (
      <article
        className={rootClasses}
        role="button"
        tabIndex={0}
        aria-labelledby={nameId}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div className="marketplace-channel-card__header">
          {channel.logoUrl ? (
            <img
              src={channel.logoUrl}
              alt={t('marketplace.channels.card.logoAlt', { name: displayName })}
              className="marketplace-channel-card__logo-img"
            />
          ) : (
            <ScopeCoinAtom
              scope={channel.scope}
              glyph={channel.glyph ?? null}
              channelName={displayName}
              size="md"
              glyphKind={channel.glyph_kind ?? 'icon'}
              hue={channel.hue}
              isSystem={channel.is_system}
              className="marketplace-channel-card__coin"
            />
          )}
          <div className="marketplace-channel-card__title-block">
            <div className="marketplace-channel-card__title-row">
              <h3 id={nameId} className="marketplace-channel-card__name">
                {displayName}
              </h3>
              {ownerStatusEntry && (
                <StatusBadgeAtom
                  tone={ownerStatusEntry.tone}
                  icon={ownerStatusEntry.icon}
                  label={t(`marketplace.common.status.${channel.status}`)}
                  size="small"
                  className="marketplace-channel-card__status-badge"
                />
              )}
            </div>
            {ownerLabel && ownerLabel.trim().length > 0 && (
              <span className="marketplace-channel-card__owner-label">
                {ownerLabel}
              </span>
            )}
          </div>
        </div>

        {displayDescription && (
          <p className="marketplace-channel-card__description">{displayDescription}</p>
        )}

        <div className="marketplace-channel-card__footer">
          <div className="marketplace-channel-card__footer-left">
            {hasRating ? (
              ratingBlock
            ) : subCount != null ? (
              <span className="marketplace-channel-card__subscriber-count">
                {t('marketplace.common.subscriberCount', { count: subCount })}
              </span>
            ) : (
              <span className="marketplace-channel-card__no-ratings">
                {t('marketplace.row.noRatings')}
              </span>
            )}
          </div>
          {subscribeButton}
        </div>
      </article>
    );
  }

  // Default: roomy
  return (
    <article
      className={rootClasses}
      role="button"
      tabIndex={0}
      aria-labelledby={nameId}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="marketplace-channel-card__top-row">
        {channel.logoUrl ? (
          <img
            src={channel.logoUrl}
            alt={t('marketplace.channels.card.logoAlt', { name: displayName })}
            className="marketplace-channel-card__logo-img"
          />
        ) : (
          <ScopeCoinAtom
            scope={channel.scope}
            glyph={channel.glyph ?? null}
            channelName={displayName}
            size="lg"
            glyphKind={channel.glyph_kind ?? 'icon'}
            hue={channel.hue}
            isSystem={channel.is_system}
            className="marketplace-channel-card__coin"
          />
        )}
        <div className="marketplace-channel-card__title-block">
          <h3 id={nameId} className="marketplace-channel-card__name">
            {displayName}
          </h3>
          {channel.publisher ? (
            <span className="marketplace-channel-card__attribution">
              <span className="marketplace-channel-card__attribution-by">by</span>
              <span className="marketplace-channel-card__attribution-name">
                {channel.publisher.display_name}
              </span>
              {channel.publisher.is_official && (
                <i
                  className="pi pi-verified marketplace-channel-card__attribution-verified"
                  aria-hidden="true"
                />
              )}
            </span>
          ) : (
            ownerLabel &&
            ownerLabel.trim().length > 0 && (
              <span className="marketplace-channel-card__owner-label">
                {ownerLabel}
              </span>
            )
          )}
        </div>
        <div className="marketplace-channel-card__top-badges">
          <ChipAtom
            label={t(`marketplace.common.scope.${channel.scope}`)}
            icon={MARKETPLACE_SCOPE_ICONS[channel.scope]}
            className={`marketplace-channel-card__scope-chip marketplace-channel-card__scope-chip--${channel.scope}`}
          />
          {channel.visibility === 'private' && (
            <StatusBadgeAtom
              tone="neutral"
              icon="pi pi-lock"
              label={t('marketplace.card.private')}
              size="small"
            />
          )}
          {ownerStatusEntry && (
            <StatusBadgeAtom
              tone={ownerStatusEntry.tone}
              icon={ownerStatusEntry.icon}
              label={t(`marketplace.common.status.${channel.status}`)}
              size="small"
              className="marketplace-channel-card__status-badge"
            />
          )}
        </div>
      </div>

      {displayDescription && (
        <p className="marketplace-channel-card__description">{displayDescription}</p>
      )}

      {tags.length > 0 && (
        <div className="marketplace-channel-card__tags">
          {visibleTags.map((tag) => (
            <ChipAtom key={tag} label={tag} className="marketplace-channel-card__tag" />
          ))}
          {overflowCount > 0 && (
            <ChipAtom
              label={t('marketplace.card.tagsOverflow', { count: overflowCount })}
              className="marketplace-channel-card__tag marketplace-channel-card__tag--overflow"
            />
          )}
        </div>
      )}

      <div className="marketplace-channel-card__footer">
        <div className="marketplace-channel-card__footer-left">
          {ratingBlock}
          {subsItemsLabel ? (
            <span className="marketplace-channel-card__subs-items">
              {subsItemsLabel}
            </span>
          ) : (
            defaultStatsNode
          )}
        </div>
        {subscribeButton}
      </div>
    </article>
  );
};

ChannelCardMolecule.displayName = 'ChannelCardMolecule';

export default ChannelCardMolecule;
